import type { ConsecutiveFiscalYearsModel } from '@/features/project/_models/consecutive';
import { t } from '@/i18n';
import {
  type AmountUnit,
  BigCalculator,
  formatAmountNumberByUnit,
  formatPercentage,
  formatValueByUnit,
  handleZeroValueWithNegativeCount,
  numberOperator,
} from '@/utils';
import Big from 'big.js';
import type { z } from 'zod';
import {
  ManagementExpenseSubsidyMonetizationStandardModifier,
  type SegmentExpenditureAndExecutionsModel,
} from '../projects/_models/segment-expenditure-and-execution';

type SegmentExpenditureAndExecutionsType = z.infer<
  typeof SegmentExpenditureAndExecutionsModel
>;

type RowHeaderKeyType =
  | 'initialBudget'
  | 'supplementaryBudget'
  | 'revenueSupplementary1BudgetAmount'
  | 'revenueSupplementary2BudgetAmount'
  | 'revenueSupplementary3BudgetAmount'
  | 'revenueSupplementary4BudgetAmount'
  | 'revenueSupplementary5BudgetAmount'
  | 'managementExpenseGrant'
  | 'subsidyMoney'
  | 'others'
  | 'totalExecutedAmount'
  | 'percentageOfManagementExpenseGrant'
  | 'managementExpenseGrantOfMonetizationCriteria'
  | 'finalizedAmount'
  | 'executedAmount'
  | 'executionRate';

export const ORDINARY_REVENUE_FINALIZED_AMOUNT_ROW_HEADER_KEYS: Array<RowHeaderKeyType> =
  [
    'initialBudget',
    'supplementaryBudget',
    'revenueSupplementary1BudgetAmount',
    'revenueSupplementary2BudgetAmount',
    'revenueSupplementary3BudgetAmount',
    'revenueSupplementary4BudgetAmount',
    'revenueSupplementary5BudgetAmount',
  ];

export const ORDINARY_REVENUE_EXECUTION_AMOUNT_ROW_HEADER_KEYS: Array<RowHeaderKeyType> =
  ['managementExpenseGrant', 'subsidyMoney', 'others', 'totalExecutedAmount'];

export const ORDINARY_REVENUE_ROW_HEADER_KEYS: Array<RowHeaderKeyType> = [
  ...ORDINARY_REVENUE_FINALIZED_AMOUNT_ROW_HEADER_KEYS,
  ...ORDINARY_REVENUE_EXECUTION_AMOUNT_ROW_HEADER_KEYS,
  'percentageOfManagementExpenseGrant',
  'managementExpenseGrantOfMonetizationCriteria',
];

export const ORDINARY_EXPENSES_ROW_HEADER_KEYS: Array<RowHeaderKeyType> = [
  'finalizedAmount',
  'executedAmount',
  'executionRate',
];

const ROW_HEADER_KEYS: Array<RowHeaderKeyType> = [
  ...ORDINARY_REVENUE_ROW_HEADER_KEYS,
  ...ORDINARY_EXPENSES_ROW_HEADER_KEYS,
];

const currentYearInputEnabledKeys: Array<RowHeaderKeyType> = [
  ...ORDINARY_REVENUE_FINALIZED_AMOUNT_ROW_HEADER_KEYS,
  'managementExpenseGrantOfMonetizationCriteria',
  'finalizedAmount',
];
const pastYearInputEnabledKeys: Array<RowHeaderKeyType> = [
  ...ORDINARY_REVENUE_EXECUTION_AMOUNT_ROW_HEADER_KEYS,
  'executedAmount',
];
const nextYearInputEnabledKeys: Array<RowHeaderKeyType> = ['initialBudget'];

type PastYearType = Record<number, string | number>;
export type SsProjectDetailedBreakdownTableDataType = {
  rowHeaderKey: RowHeaderKeyType;
  level1: string | null;
  level2: string | null;
  level3: string | null;
  level4: string | null;
  children?: [] | null;
  currentYear: string | null;
  nextYear: string | null;
  yearStatus: {
    pastYearDisabled?: boolean;
    currentYearDisabled?: boolean;
    nextYearDisabled?: boolean;
  };
} & PastYearType;

type GetYearDataArgs = {
  type: 'currentYear' | 'nextYear' | 'pastYear';
};

type GetTableDataSourcesArgs = {
  key: RowHeaderKeyType;
  pastYears: number[];
  fiscalYear: number | undefined;
  data: SegmentExpenditureAndExecutionsType | undefined;
  unit: AmountUnit;
  isDebugMode: boolean;
};

const getIsDisabledStatus = ({
  type,
  key,
}: GetYearDataArgs & { key: RowHeaderKeyType }): boolean => {
  switch (type) {
    case 'pastYear': {
      return !pastYearInputEnabledKeys.includes(key);
    }
    case 'currentYear': {
      return !currentYearInputEnabledKeys.includes(key);
    }
    case 'nextYear': {
      return !nextYearInputEnabledKeys.includes(key);
    }
    default: {
      const unusedType: never = type;
      throw new Error(`unused type: ${unusedType}`);
    }
  }
};

const formatFinalizedAmount = (
  value: number | null | undefined,
  unit: AmountUnit,
) => {
  return formatValueByUnit(value, {
    operation: 'round',
    unit,
  });
};

const formatExecutedAmount = (
  value: number | null | undefined,
  unit: AmountUnit,
) => {
  return formatValueByUnit(value, {
    operation: 'floor',
    unit,
  });
};

const getYearData = ({
  currentYearData,
  key,
  unit,
  isDebugMode,
}: {
  currentYearData: SegmentExpenditureAndExecutionsType[number] | undefined;
} & Pick<GetTableDataSourcesArgs, 'key' | 'unit' | 'isDebugMode'>) => {
  if (!currentYearData) {
    return null;
  }
  if (key === 'managementExpenseGrantOfMonetizationCriteria') {
    return currentYearData.revenueManagementExpenseSubsidyMonetizationStandards
      ? currentYearData.revenueManagementExpenseSubsidyMonetizationStandards
          .map((v) => ManagementExpenseSubsidyMonetizationStandardModifier[v])
          .join('\n')
      : null;
  }
  if (key === 'initialBudget') {
    return formatFinalizedAmount(
      currentYearData.revenueInitialBudgetAmount,
      unit,
    );
  }
  if (key === 'supplementaryBudget') {
    const revenueSupplementary = [
      currentYearData.revenueSupplementary1BudgetAmount,
      currentYearData.revenueSupplementary2BudgetAmount,
      currentYearData.revenueSupplementary3BudgetAmount,
      currentYearData.revenueSupplementary4BudgetAmount,
      currentYearData.revenueSupplementary5BudgetAmount,
    ];
    if (revenueSupplementary.every((v) => typeof v !== 'number')) {
      return null;
    }
    const totalSupplementaryBudget = revenueSupplementary.reduce<number>(
      (acc, cur) => BigCalculator.add(acc, cur ?? 0),
      0,
    );

    if (revenueSupplementary.some((v) => typeof v === 'number' && v < 0)) {
      const { isAggregateValueZero, fallbackValue } =
        handleZeroValueWithNegativeCount(totalSupplementaryBudget, {
          negativeCount: 1,
          isDebugMode,
        });
      if (isAggregateValueZero) {
        return fallbackValue;
      }
    }

    return formatFinalizedAmount(totalSupplementaryBudget, unit);
  }
  if (key === 'revenueSupplementary1BudgetAmount') {
    return formatFinalizedAmount(
      currentYearData.revenueSupplementary1BudgetAmount,
      unit,
    );
  }
  if (key === 'revenueSupplementary2BudgetAmount') {
    return formatFinalizedAmount(
      currentYearData.revenueSupplementary2BudgetAmount,
      unit,
    );
  }
  if (key === 'revenueSupplementary3BudgetAmount') {
    return formatFinalizedAmount(
      currentYearData.revenueSupplementary3BudgetAmount,
      unit,
    );
  }
  if (key === 'revenueSupplementary4BudgetAmount') {
    return formatFinalizedAmount(
      currentYearData.revenueSupplementary4BudgetAmount,
      unit,
    );
  }
  if (key === 'revenueSupplementary5BudgetAmount') {
    return formatFinalizedAmount(
      currentYearData.revenueSupplementary5BudgetAmount,
      unit,
    );
  }
  if (key === 'finalizedAmount') {
    return formatFinalizedAmount(currentYearData.expenditureBudgetAmount, unit);
  }

  if (key === 'managementExpenseGrant') {
    return formatExecutedAmount(
      currentYearData.revenueManagementExpenseExecutedAmount,
      unit,
    );
  }
  if (key === 'subsidyMoney') {
    return formatExecutedAmount(
      currentYearData.revenueSubsidyExecutedAmount,
      unit,
    );
  }
  if (key === 'others') {
    return formatExecutedAmount(
      currentYearData.revenueOthersExecutedAmount,
      unit,
    );
  }
  if (key === 'totalExecutedAmount') {
    const result = getTotalExecutedAmount(
      currentYearData,
      'totalExecutedAmount',
      isDebugMode,
    );

    if (result?.isAggregateValueZero) {
      return result.value;
    }
    return formatExecutedAmount(result?.value, unit);
  }
  if (
    key === 'percentageOfManagementExpenseGrant' &&
    currentYearData.revenueManagementExpenseExecutedAmount
  ) {
    const result = getTotalExecutedAmount(
      currentYearData,
      'percentageOfManagementExpenseGrant',
    );

    return formatPercentage(
      BigCalculator.divide(
        formatAndFloorThousandUnitValue(
          currentYearData.revenueManagementExpenseExecutedAmount,
        ) ?? 0,
        // percentageOfManagementExpenseGrantの場合は素直にgetTotalExecutedAmountの計算結果を返しているためnumber型にアサーションしている。
        result?.value as number | null,
      ),
      { maximumFractionDigits: 1 },
    );
  }
  if (key === 'executedAmount') {
    return formatExecutedAmount(
      currentYearData.expenditureExecutedAmount,
      unit,
    );
  }
  if (key === 'executionRate') {
    if (!currentYearData.expenditureBudgetAmount) {
      return null;
    }
    if (!currentYearData.expenditureExecutedAmount) {
      if (currentYearData.expenditureExecutedAmount === 0) {
        return formatPercentage(0, {
          maximumFractionDigits: 1,
        });
      }
      return null;
    }

    return formatPercentage(
      BigCalculator.divide(
        currentYearData.expenditureExecutedAmount,
        currentYearData.expenditureBudgetAmount,
      ),
      { maximumFractionDigits: 1 },
    );
  }

  return null;
};

const getNextYearData = ({
  data,
  key,
  unit,
}: { data: SegmentExpenditureAndExecutionsType[number] | undefined } & Pick<
  GetTableDataSourcesArgs,
  'key' | 'unit'
>) => {
  if (nextYearInputEnabledKeys.includes(key)) {
    return formatFinalizedAmount(data?.revenueEstimatedAmountForNextYear, unit);
  }
  return null;
};

const formatAndFloorThousandUnitValue = (value: number | undefined | null) => {
  if (!value) {
    return value;
  }
  return (
    numberOperator(formatAmountNumberByUnit(value, 'thousand'), 'floor') * 1_000
  );
};

const getTotalExecutedAmount = (
  data: SegmentExpenditureAndExecutionsType[number],
  type: 'totalExecutedAmount' | 'percentageOfManagementExpenseGrant',
  isDebugMode?: boolean,
):
  | { value: string; isAggregateValueZero: true }
  | { value: number; isAggregateValueZero: false }
  | null => {
  const targets = [
    data.revenueManagementExpenseExecutedAmount,
    data.revenueSubsidyExecutedAmount,
    data.revenueOthersExecutedAmount,
  ];
  if (targets.every((v) => typeof v !== 'number')) {
    return null;
  }

  const value = Big(
    formatAndFloorThousandUnitValue(
      data.revenueManagementExpenseExecutedAmount,
    ) ?? 0,
  )
    .add(
      formatAndFloorThousandUnitValue(data.revenueSubsidyExecutedAmount) ?? 0,
    )
    .add(formatAndFloorThousandUnitValue(data.revenueOthersExecutedAmount) ?? 0)
    .toNumber();

  if (
    type === 'totalExecutedAmount' &&
    targets.some((v) => typeof v === 'number' && v < 0)
  ) {
    const { isAggregateValueZero, fallbackValue } =
      handleZeroValueWithNegativeCount(value, {
        negativeCount: 1,
        isDebugMode,
      });
    if (isAggregateValueZero) {
      return {
        value: fallbackValue,
        isAggregateValueZero,
      };
    }
  }

  return {
    value,
    isAggregateValueZero: false,
  };
};

const getPastYearData = ({
  data,
  key,
  unit,
  pastYears,
  isDebugMode,
}: Pick<
  GetTableDataSourcesArgs,
  'data' | 'key' | 'unit' | 'pastYears' | 'isDebugMode'
>) => {
  const yearData = pastYears.map((year) => {
    const currentYearData = data?.find((v) => v.fiscalYear === year);
    return {
      [year]: getYearData({
        currentYearData,
        key,
        unit,
        isDebugMode,
      }),
    };
  });
  return Object.assign({}, ...yearData);
};

const getTableDataSources = ({
  key,
  data,
  fiscalYear,
  unit,
  pastYears,
  isDebugMode,
}: GetTableDataSourcesArgs): SsProjectDetailedBreakdownTableDataType => {
  const currentYearData = data?.find((v) => v.fiscalYear === fiscalYear);

  const yearStatus = {
    pastYearDisabled: getIsDisabledStatus({
      type: 'pastYear',
      key,
    }),
    currentYearDisabled: getIsDisabledStatus({
      type: 'currentYear',
      key,
    }),
    nextYearDisabled: getIsDisabledStatus({
      type: 'nextYear',
      key,
    }),
  };

  switch (key) {
    case 'initialBudget':
    case 'supplementaryBudget':
    case 'revenueSupplementary1BudgetAmount':
    case 'revenueSupplementary2BudgetAmount':
    case 'revenueSupplementary3BudgetAmount':
    case 'revenueSupplementary4BudgetAmount':
    case 'revenueSupplementary5BudgetAmount':
    case 'managementExpenseGrant':
    case 'subsidyMoney':
    case 'others':
    case 'totalExecutedAmount':
    case 'percentageOfManagementExpenseGrant':
    case 'managementExpenseGrantOfMonetizationCriteria': {
      const level1Label = t('ordinary-revenue');
      if (ORDINARY_REVENUE_FINALIZED_AMOUNT_ROW_HEADER_KEYS.includes(key)) {
        return {
          rowHeaderKey: key,
          level1: level1Label,
          level2: t('finalized-amount'),
          level3: t('management-expense-grant'),
          level4: rowHeaderLabelConverter[key],
          currentYear: getYearData({
            currentYearData,
            key,
            unit,
            isDebugMode,
          }),
          nextYear: getNextYearData({ data: currentYearData, key, unit }),
          ...getPastYearData({ data, key, unit, pastYears, isDebugMode }),
          yearStatus,
        };
      }
      if (ORDINARY_REVENUE_EXECUTION_AMOUNT_ROW_HEADER_KEYS.includes(key)) {
        return {
          rowHeaderKey: key,
          level1: level1Label,
          level2: t('execution-amount'),
          level3: rowHeaderLabelConverter[key],
          level4: null,
          currentYear: getYearData({
            currentYearData,
            key,
            unit,
            isDebugMode,
          }),
          nextYear: getNextYearData({ data: currentYearData, key, unit }),
          ...getPastYearData({ data, key, unit, pastYears, isDebugMode }),
          yearStatus,
        };
      }
      return {
        rowHeaderKey: key,
        level1: level1Label,
        level2: rowHeaderLabelConverter[key],
        level3: null,
        level4: null,
        currentYear: getYearData({
          currentYearData,
          key,
          unit,
          isDebugMode,
        }),
        nextYear: getNextYearData({ data: currentYearData, key, unit }),
        ...getPastYearData({ data, key, unit, pastYears, isDebugMode }),
        yearStatus,
      };
    }
    case 'finalizedAmount':
    case 'executedAmount':
    case 'executionRate': {
      return {
        rowHeaderKey: key,
        level1: t('ordinary-expenses'),
        level2: rowHeaderLabelConverter[key],
        level3: null,
        level4: null,
        currentYear: getYearData({
          currentYearData,
          key,
          unit,
          isDebugMode,
        }),
        nextYear: getNextYearData({ data: currentYearData, key, unit }),
        ...getPastYearData({ data, key, unit, pastYears, isDebugMode }),
        yearStatus,
      };
    }
    default: {
      const unusedKey: never = key;
      throw new Error(`unused key: ${unusedKey}`);
    }
  }
};

const FUll_SPACE_TEXT = '　';

const rowHeaderLabelConverter: Record<RowHeaderKeyType, string> = {
  initialBudget: t('initial-budget'),
  supplementaryBudget: t('supplementary-budget'),
  revenueSupplementary1BudgetAmount: `${FUll_SPACE_TEXT}${t('supplementary1-budget')}`,
  revenueSupplementary2BudgetAmount: `${FUll_SPACE_TEXT}${t('supplementary2-budget')}`,
  revenueSupplementary3BudgetAmount: `${FUll_SPACE_TEXT}${t('supplementary3-budget')}`,
  revenueSupplementary4BudgetAmount: `${FUll_SPACE_TEXT}${t('supplementary4-budget')}`,
  revenueSupplementary5BudgetAmount: `${FUll_SPACE_TEXT}${t('supplementary5-budget')}`,
  managementExpenseGrant: t('management-expense-grant'),
  subsidyMoney: t('subsidy-money'),
  others: t('others'),
  totalExecutedAmount: t('total-executed-amount'),
  percentageOfManagementExpenseGrant: t(
    'percentage-of-management-expense-grant',
  ),
  managementExpenseGrantOfMonetizationCriteria: t(
    'management-expense-grant-of-monetization-criteria',
  ),
  finalizedAmount: t('finalized-amount'),
  executedAmount: t('execution-amount'),
  executionRate: t('execution-rate'),
};

type Args = {
  data: SegmentExpenditureAndExecutionsType | undefined;
  consecutiveFiscalYears:
    | z.infer<typeof ConsecutiveFiscalYearsModel>
    | undefined;
  projectYear: number | undefined;
  unit: AmountUnit;
  isDebugMode: boolean;
};

export const getSsProjectDetailedBreakdownTableSource = ({
  data,
  projectYear,
  consecutiveFiscalYears,
  unit,
  isDebugMode,
}: Args) => {
  const pastYears = projectYear
    ? (consecutiveFiscalYears?.flatMap((v) =>
        v.fiscalYear < projectYear ? v.fiscalYear : [],
      ) ?? [])
    : [];

  const dataSources = ROW_HEADER_KEYS.map((key) =>
    getTableDataSources({
      key,
      fiscalYear: projectYear,
      pastYears,
      data,
      unit,
      isDebugMode,
    }),
  );

  return { dataSources, pastYears };
};
